import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUsers from "../../hooks/utilisateur/useUsers";
import Loader from "../components/loader";

import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import { defaultImage } from "../../utils/ImageEnBase46";
import { FiSearch, FiFilter } from "react-icons/fi";

function UsersList() {
  const {
    users,
    setUsers,
    updateUser,
    generateFile,
    getOneUser,
    checkBuildStatus,
    fetchUsers,
  } = useUsers();

  const navigate = useNavigate();

  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const usersPerPage = 10;

  const [isBuilding, setIsBuilding] = useState(false);

  useEffect(() => {
    localStorage.setItem("isBuilding", isBuilding);
  }, [isBuilding]);

  // ================= FETCH USERS =================

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        await fetchUsers();
      } catch (error) {
        console.error("Erreur chargement utilisateurs :", error);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  // ================= BUILD STATUS =================

  useEffect(() => {
    const fetchBuildStatus = () => {
      checkBuildStatus((serverIsBuilding) => {
        setIsBuilding(serverIsBuilding);
      });
    };

    fetchBuildStatus();

    const interval = setInterval(fetchBuildStatus, 10000);

    return () => clearInterval(interval);
  }, []);

  // ================= UPDATE BUILD STATUS =================

  useEffect(() => {
    const interval = setInterval(() => {
      users.forEach((user) => {
        if (user.buildStatus === "building") {
          getOneUser(user._id, (userData) => {
            if (userData?.data?.buildStatus === "done") {
              setUsers((prevUsers) =>
                prevUsers.map((u) =>
                  u._id === user._id ? { ...u, ...userData.data } : u,
                ),
              );
            }
          });
        }
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [users, getOneUser]);

  // ================= NAVIGATION =================

  const navigateToCreateUser = () => {
    navigate("/create-user");
  };

  // ================= DELETE USER =================

  const handleDeleteUser = (userId) => {
    confirmAlert({
      title: "Confirmation",
      message: "Êtes-vous sûr de vouloir supprimer cet utilisateur ?",
      buttons: [
        {
          label: "Oui",
          onClick: () => {
            updateUser(userId, { active: 0 }, () => {
              setUsers((prevUsers) =>
                prevUsers.map((user) =>
                  user._id === userId ? { ...user, active: 0 } : user,
                ),
              );

              setMessage({
                type: "success",
                text: "Utilisateur supprimé avec succès.",
              });

              setTimeout(() => setMessage(null), 2000);
            });
          },
        },
        {
          label: "Non",
        },
      ],
    });
  };

  // ================= GENERATE FILE =================

  const handleGenerateFile = (userId) => {
    if (isBuilding) return;

    setIsBuilding(true);

    localStorage.setItem("isBuilding", "true");
    localStorage.setItem("buildingUserId", userId);

    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user._id === userId ? { ...user, buildStatus: "building" } : user,
      ),
    );

    updateUser(userId, { buildStatus: "building" }, () => {
      const data = { clientId: userId };

      generateFile(data);

      const intervalId = setInterval(() => {
        getOneUser(userId, (userData) => {
          if (userData?.data?.buildStatus === "done") {
            clearInterval(intervalId);

            setIsBuilding(false);

            localStorage.setItem("isBuilding", "false");
            localStorage.removeItem("buildingUserId");

            setUsers((prevUsers) =>
              prevUsers.map((user) =>
                user._id === userId ? { ...user, ...userData.data } : user,
              ),
            );
          }
        });
      }, 5000);
    });
  };

  // ================= FILTER USERS =================

  const activeUsers = users
    .filter((user) => user.active === 1)
    .filter((user) => {
      const fullText = `${user.nom} ${user.prenom} ${user.email} ${
        user.entreprise?.nom || ""
      }`.toLowerCase();

      return fullText.includes(searchTerm.toLowerCase());
    })
    .filter((user) => {
      if (roleFilter === "all") return true;

      return user.role === Number(roleFilter);
    });

  // ================= PAGINATION =================

  const totalPages = Math.ceil(activeUsers.length / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = activeUsers.slice(indexOfFirstUser, indexOfLastUser);
  console.log(currentUsers);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // ================= RENDER =================

  return (
    <div className="container-fluid">
      {/* TITLE */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="h4 mb-0">Utilisateurs</h2>
      </div>

      {/* ALERT MESSAGE */}
      {message && (
        <div
          className={`alert ${
            message.type === "success" ? "alert-success" : "alert-danger"
          }`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h3 className="h6 mb-0">Liste des utilisateurs</h3>
        </div>

        <div className="card-body">
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                {/* SEARCH */}
                <div className="position-relative" style={{ width: "300px" }}>
                  <FiSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-secondary" />
                  <input
                    type="text"
                    className="form-control ps-4 rounded-pill"
                    placeholder="Rechercher utilisateur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: "35px" }}
                  />
                </div>

                {/* FILTER */}
                <div className="d-flex align-items-center gap-2">
                  <FiFilter className="text-secondary" />
                  <select
                    className="form-select rounded-pill"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    style={{ width: "180px" }}
                  >
                    <option value="all">Tous les rôles</option>
                    <option value="1">Admin</option>
                    <option value="2">Superviseur</option>
                    <option value="3">Client</option>
                  </select>
                </div>
              </div>

              {/* TABLE */}
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>id</th>
                      <th>Nom</th>
                      <th>Prénom</th>
                      <th>Email</th>
                      <th>Société</th>
                      <th>Rôle</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentUsers.map((user) => (
                      <tr key={user._id}>
                        <td className="text-center">
                          <img
                            src={
                              user?.profil?.startsWith("data:")
                                ? user?.profil
                                : defaultImage
                            }
                            alt="Logo"
                            className="rounded-circle"
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              border: "1px solid #ccc",
                            }}
                          />
                        </td>

                        <td>{user._id}</td>
                        <td>{user.nom}</td>
                        <td>{user.prenom}</td>
                        <td>{user.email}</td>
                        <td>{user.entreprise?.nom || "_"}</td>

                        <td>
                          {user.role === 1
                            ? "Admin"
                            : user.role === 2
                              ? "Superviseur"
                              : user.role === 3
                                ? "Client"
                                : "Inconnu"}
                        </td>

                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => navigate(`/edit-user/${user._id}`)}
                            >
                              Modifier
                            </button>

                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteUser(user._id)}
                            >
                              Supprimer
                            </button>

                            {user.buildStatus === "idle" && (
                              <button
                                style={{ width: "100px" }}
                                className="btn btn-success btn-sm"
                                onClick={() => handleGenerateFile(user._id)}
                                disabled={isBuilding}
                              >
                                Générer
                              </button>
                            )}

                            {user.buildStatus === "building" && (
                              <button
                                style={{ width: "100px" }}
                                className="btn btn-warning btn-sm"
                                disabled
                              >
                                <span
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                En cours...
                              </button>
                            )}

                            {user.buildStatus === "done" && (
                              <button
                                style={{ width: "100px" }}
                                className="btn btn-secondary btn-sm"
                                onClick={() =>
                                  (window.location.href = `${process.env.REACT_APP_API_URL_DOWNLOAD_FILE}/download/${user._id}`)
                                }
                              >
                                Télécharger
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>

                <span className="small text-secondary">
                  Page {currentPage} sur {totalPages}
                </span>

                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Suivant
                </button>
              </div>

              {/* CREATE USER */}
              <div className="text-center mt-3">
                <button
                  className="btn btn-success btn-sm"
                  onClick={navigateToCreateUser}
                >
                  Créer Utilisateur
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default UsersList;
